/**
 * VrboSection — VRBO-specific Owner Payout label and tooltip copy for BookingDetailModal
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 * Phase 3b extracted from BookingDetailModal.jsx; JSX and strings unchanged.
 *
 * Purpose:
 *   Render VRBO-specific folio label content (Legacy iCal, pre-stay estimated vs final) and provide
 *   tooltip copy for VRBO iCal / pre-stay / Stripe. Presentational only; parent passes theme,
 *   variant, hostPayout; parent owns predicate flags and tooltip show/hide.
 *
 * Assumptions & Invariants:
 *   - Presentational; no fetch; no admin imports.
 *   - Receives already-resolved inputs; uses variant from parent (legacyIcal | prestay).
 *   - getVrboPayoutTooltipText is pure: same inputs → same string. Call only when at least one
 *     VRBO flag is true (isLegacyVRBOiCal | isVRBONonICalComputed | isVRBOStripe).
 *
 * Dependencies: React only
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 3b extraction from BookingDetailModal (VRBO label + tooltip helper).
 */

const labelSubtextStyle = {
  fontSize: '12px',
  fontStyle: 'italic',
  opacity: 0.8,
  marginLeft: '4px',
};

/** Crissy: integrated VRBO (>30 days to check-in). Time-based rule. */
const VRBO_ESTIMATED_PAYOUT_TOOLTIP = 'Estimated Payout Amount. Final amount will update once all guest payments are completed (30 days prior to check in).';

/** Crissy: integrated VRBO (≤30 days to check-in). Time-based rule. */
const VRBO_FINAL_PAYOUT_TOOLTIP = 'VRBO payout calculated using the standard payout formula based on available payment data.';

/**
 * Returns VRBO-specific tooltip string for Owner Payout info icon.
 * Integrated VRBO (non-iCal): time-based rule via daysUntilCheckIn (>30 vs ≤30). Legacy iCal and Stripe unchanged.
 * @param {boolean} isFinalCopy
 * @param {boolean} isLegacyVRBOiCal
 * @param {boolean} isVRBONonICalComputed
 * @param {boolean} isVRBOStripe
 * @param {number | null} [daysUntilCheckIn]
 * @param {boolean} [isIntegratedVrboNonIcal]
 * @returns {string | null}
 */
export function getVrboPayoutTooltipText(isFinalCopy, isLegacyVRBOiCal, isVRBONonICalComputed, isVRBOStripe, daysUntilCheckIn = null, isIntegratedVrboNonIcal = false) {
  if (isIntegratedVrboNonIcal) {
    if (daysUntilCheckIn != null) {
      if (daysUntilCheckIn > 30) return VRBO_ESTIMATED_PAYOUT_TOOLTIP;
      return VRBO_FINAL_PAYOUT_TOOLTIP;
    }
    return VRBO_FINAL_PAYOUT_TOOLTIP;
  }
  if (isFinalCopy) {
    if (isVRBONonICalComputed) return VRBO_FINAL_PAYOUT_TOOLTIP;
    if (isLegacyVRBOiCal) return 'Legacy VRBO iCal bookings use a computed payout based on imported fields; actual deposit timing may vary.';
    if (isVRBOStripe) return 'VRBO Stripe payout is sourced from Hostaway net deposit (after Stripe processing fees). Payout timing depends on channel settlement.';
  } else {
    if (isLegacyVRBOiCal) return 'Legacy VRBO iCal bookings use a computed payout based on imported fields; actual deposit timing may vary.';
    if (isVRBONonICalComputed) return 'VRBO (pre-stay): Estimated payout calculated using pre-stay formula. Based on payments processed so far and may change. Actual payout will be confirmed after stay completion.';
    if (isVRBOStripe) return 'VRBO Stripe payout is sourced from Hostaway net deposit (after Stripe processing fees). Payout timing depends on channel settlement.';
  }
  return null;
}

/**
 * Renders VRBO-specific Owner Payout label content (text + optional parenthetical).
 * @param {{ theme?: string, variant: 'legacyIcal' | 'prestay', hostPayout?: { copyMode?: string, payoutDisplayMode?: string, isProvisionalCopy?: boolean } }}
 */
export default function VrboSection({ theme, variant, hostPayout }) {
  if (variant === 'legacyIcal') {
    return (
      <>
        Paid to Owner
        <span style={labelSubtextStyle}>
          (Legacy VRBO iCal payout - computed)
        </span>
      </>
    );
  }
  if (variant === 'prestay') {
    const copyMode = hostPayout?.copyMode || hostPayout?.payoutDisplayMode || 'provisional';
    const isProvisional = copyMode === 'provisional' || (copyMode === 'estimated' && hostPayout?.isProvisionalCopy !== false);
    if (isProvisional) {
      return (
        <>
          Estimated payout (pre-stay)
          <span style={labelSubtextStyle}>
            (VRBO pre-stay estimate)
          </span>
        </>
      );
    }
    return 'Payout';
  }
  return null;
}
